import { useNavigate } from "react-router";
import { ArrowRight, CalendarDays, CalendarRange, CheckCircle2, ListChecks } from "lucide-react";
import { cn } from "@/lib/cn";
import { useEmOutraOrganizacao } from "@/components/organization/painel/OrganizacaoEmFoco";
import {
  rotuloDeExpediente,
  situacaoDoDia,
  type Expediente,
  type OperacaoDoMembro,
} from "./situacao-do-dia";
import { ChipsDeEspecialidade, FotoDoMembro, NumeroDoMembro } from "./partes-do-cartao";

export type MembroDoDia = {
  membershipId: string;
  userId: string;
  nome: string;
  avatarUrl: string | null;
  funcao: string;
  especialidades: string[];
  ehProfissional: boolean;
  suspenso: boolean;
  /** Serviços que o membro assume — as "responsabilidades" já geridas na aba de acessos. */
  responsabilidades: number;
};

/**
 * O profissional como cartão da Visão geral: quem é, como está agora e quanto está carregando.
 *
 * Dois formatos no mesmo esqueleto — quem atende mostra números da agenda, quem não atende mostra
 * função, responsabilidades e expediente. Apoio NÃO ganha "0 consultas": zero aqui não é um dado, é
 * a ausência de um dado que não se aplica, e exibi-lo faria a recepcionista parecer ociosa.
 *
 * Os cartões da linha terminam juntos: o cartão ocupa a altura da célula da grade.
 */
export function ProfissionalDoDiaCard({
  membro,
  operacao,
  expediente,
  podeVerAgenda,
  onAbrir,
}: {
  membro: MembroDoDia;
  operacao?: OperacaoDoMembro;
  expediente: Expediente;
  /** Sem isso o botão "Ver agenda" some — levaria a uma agenda que a pessoa não pode enxergar. */
  podeVerAgenda: boolean;
  onAbrir?: () => void;
}) {
  const navigate = useNavigate();
  const emOutraOrganizacao = useEmOutraOrganizacao();
  // Quem não atende não tem "situação de agenda" — dizer "Fora do expediente" pra recepção seria
  // aplicar uma régua que não é a dela. O selo então mostra o status do MEMBRO.
  const situacao = membro.suspenso
    ? { chave: "suspenso" as const, rotulo: "Acesso suspenso", cor: "#dc2626", fundo: "#fef2f2" }
    : membro.ehProfissional
      ? situacaoDoDia(operacao, expediente)
      : { chave: "ativo" as const, rotulo: "Ativo", cor: "#16a34a", fundo: "#f0fdf4" };
  const proxima = operacao?.proxima;

  return (
    <div
      role={onAbrir ? "button" : undefined}
      tabIndex={onAbrir ? 0 : undefined}
      onClick={onAbrir}
      onKeyDown={(e) => {
        if (!onAbrir) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onAbrir();
        }
      }}
      aria-label={onAbrir ? `Abrir perfil de ${membro.nome}` : undefined}
      className={cn(
        "flex h-full min-h-[212px] flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card",
        membro.suspenso && "opacity-70",
        onAbrir &&
          "cursor-pointer transition-colors hover:border-brand-200 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:hover:border-border",
      )}
    >
      {/* Mesmo desenho do topo do Meu perfil: faixa clara, foto quadrada, papel em selo. */}
      <div className="flex items-start gap-3 border-b border-brand-100 bg-brand-50 px-4 py-3 dark:border-primary/20 dark:bg-primary/10">
        <FotoDoMembro src={membro.avatarUrl} nome={membro.nome} />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-start justify-between gap-2">
            <span className="inline-flex truncate rounded-md bg-brand-100 px-2 py-0.5 text-[10px] font-semibold text-brand-700 dark:bg-primary/20 dark:text-primary">
              {membro.funcao}
            </span>
            <span
              className="max-w-[55%] shrink-0 rounded-md px-2 py-0.5 text-right text-[10px] font-bold uppercase leading-tight tracking-wide"
              style={{ backgroundColor: situacao.fundo, color: situacao.cor }}
            >
              {situacao.rotulo}
            </span>
          </div>
          <p className="truncate text-base font-bold text-zinc-900 dark:text-foreground">{membro.nome}</p>
          <ChipsDeEspecialidade especialidades={membro.especialidades} />
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-4">
        {membro.ehProfissional ? (
          <>
            <div className="grid grid-cols-3 gap-2">
              <NumeroDoMembro valor={operacao?.hoje ?? 0} rotulo="hoje" icone={CalendarDays} cor="#6366f1" />
              <NumeroDoMembro valor={operacao?.concluidasHoje ?? 0} rotulo="concluídas" icone={CheckCircle2} cor="#10b981" />
              <NumeroDoMembro valor={operacao?.noMes ?? 0} rotulo="no mês" icone={CalendarRange} cor="#2457ff" />
            </div>

            <div className="mt-auto flex items-end justify-between gap-2">
              <p className="min-w-0 flex-1 truncate text-[11px] text-zinc-500 dark:text-muted-foreground">
                {proxima
                  ? proxima.patientName
                    ? `Próxima: ${proxima.startTime} · ${proxima.patientName}`
                    : `Próxima: ${proxima.startTime}`
                  : "Sem próximas consultas hoje"}
              </p>
              {podeVerAgenda && !emOutraOrganizacao && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/agenda?profissional=${membro.userId}`);
                  }}
                  aria-label={`Ver a agenda de ${membro.nome}`}
                  className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2.5 text-[11px] font-semibold text-brand-700 shadow-sm transition-colors hover:border-brand-300 hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-border dark:bg-card dark:text-primary dark:hover:bg-primary/10"
                >
                  <CalendarDays className="h-3.5 w-3.5" />
                  Ver agenda
                  <ArrowRight className="h-3 w-3" />
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-1 flex-col gap-2 text-[11px] text-zinc-500 dark:text-muted-foreground">
            <div className="flex items-center gap-2 rounded-lg border border-zinc-100 px-2.5 py-2 dark:border-border">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber-500/10 text-amber-600">
                <ListChecks className="h-3.5 w-3.5" />
              </span>
              {membro.responsabilidades > 0
                ? `${membro.responsabilidades} ${membro.responsabilidades === 1 ? "responsabilidade atribuída" : "responsabilidades atribuídas"}`
                : "Sem responsabilidades atribuídas"}
            </div>
            <div className="mt-auto space-y-0.5 text-zinc-400 dark:text-muted-foreground/70">
              {/* Não há jornada por membro no modelo — o expediente aqui é o da organização, e só
                  aparece quando ela realmente configurou a agenda. */}
              <p className="truncate">{rotuloDeExpediente(expediente) ?? "Expediente não configurado"}</p>
              <p>Não atende na agenda — as métricas de consulta não se aplicam.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
